import { computed, onBeforeUnmount, ref, toValue, watch, type MaybeRefOrGetter } from "vue";

type DeferredUpdateAction = () => void | Promise<void>;

const updateBlockers = ref<Record<string, string>>({});
const pendingUpdateAction = ref<DeferredUpdateAction | null>(null);
const isApplyingUpdate = ref(false);

const normalizeKey = (key: string) => key.trim();

const hasBlockers = computed(() => Object.keys(updateBlockers.value).length > 0);
const blockerReasons = computed(() =>
  Object.values(updateBlockers.value).filter(reason => reason.length > 0)
);

const blockAppUpdate = (key: string, reason = "") => {
  const blockerKey = normalizeKey(key);
  if (!blockerKey) return;

  updateBlockers.value = {
    ...updateBlockers.value,
    [blockerKey]: reason,
  };
};

const unblockAppUpdate = (key: string) => {
  const blockerKey = normalizeKey(key);
  if (!blockerKey || !updateBlockers.value[blockerKey]) return;

  const nextBlockers = { ...updateBlockers.value };
  delete nextBlockers[blockerKey];
  updateBlockers.value = nextBlockers;
};

const setAppUpdateBlocker = (key: string, blocked: boolean, reason = "") => {
  if (blocked) {
    blockAppUpdate(key, reason);
    return;
  }

  unblockAppUpdate(key);
};

const tryApplyPendingUpdate = async () => {
  if (!pendingUpdateAction.value || hasBlockers.value || isApplyingUpdate.value)
    return false;

  isApplyingUpdate.value = true;
  const apply = pendingUpdateAction.value;
  pendingUpdateAction.value = null;

  try {
    await apply();
    return true;
  } finally {
    isApplyingUpdate.value = false;
  }
};

const queueDeferredUpdate = (apply: DeferredUpdateAction) => {
  pendingUpdateAction.value = apply;
  void tryApplyPendingUpdate();
};

export const useDeferredAppUpdate = () => ({
  hasBlockers,
  blockerReasons,
  isApplyingUpdate,
  queueDeferredUpdate,
  tryApplyPendingUpdate,
  setAppUpdateBlocker,
  blockAppUpdate,
  unblockAppUpdate,
});

export const useUpdateBlocker = (
  key: string,
  blocked: MaybeRefOrGetter<boolean>,
  reason = ""
) => {
  watch(
    () => toValue(blocked),
    nextValue => {
      setAppUpdateBlocker(key, Boolean(nextValue), reason);
    },
    { immediate: true }
  );

  onBeforeUnmount(() => {
    unblockAppUpdate(key);
  });
};